import {
  FC,
  JSX,
  useEffect,
  useMemo,
  useState
} from "react";
import {
  Text,
  ScrollView,
  StyleSheet,
} from "react-native";
import { useCreateReportMutation } from "@/_store/company/company.slice";
import { useGetCurrentUserQuery } from "@/_store/currentUser/currentUser.slice";
import { useUpdateReportMutation } from "@/_store/report/report.slice";
import SuccessCompanyCheck from "@/app/(main)/successCompanyCheck/SuccessCompanyCheck";
import CompanyCheck from "@/app/(main)/companyCheck/CompanyCheck";
import IndividualCheck from "@/app/(main)/general/individualCheck/IndividualCheck";
import SubscriptionCheck from "@/app/(main)/subscriptionCheck/SubscriptionCheck";

interface ISheetContentRenderer {
  activeSheet: number | null;
}

export const SHEET = {
  SUBSCRIPTION_MONITORING: 1,
  COMPANY_CHECK: 2,
  INDIVIDUAL_CHECK: 3,
} as const;

const SheetContentRenderer: FC<ISheetContentRenderer> = ({
  activeSheet
}) => {

  const [createReport, { isLoading, data: createData }] = useCreateReportMutation();
  const { data: subscribeData } = useGetCurrentUserQuery();
  const [updateReport, { isLoading: isRefreshReport, data: refreshData }] = useUpdateReportMutation();
  const [reportId, setReportId] = useState<string | null>(null);

  useEffect(() => {
    const newReportId = refreshData?.id ?? createData?.id ?? null;
    setReportId(newReportId);
  }, [createData, refreshData]);

  useEffect(() => {
    if (activeSheet === null) setReportId(null);
  }, [activeSheet]);


  const renderSubscriptionCheck = (): JSX.Element => (
    <ScrollView style={styles.scrollView} contentContainerStyle={styles.content}>
      <SubscriptionCheck />
    </ScrollView>
  );

  const renderCompanyCheck = (): JSX.Element => (
    <ScrollView style={styles.scrollView} contentContainerStyle={styles.content}>
      {reportId ? (
        <SuccessCompanyCheck
          reportId={reportId}
          updateReport={updateReport}
          isRefreshReport={isRefreshReport}
        />
      ) : (
        <CompanyCheck createReport={createReport} isLoading={isLoading} />
      )}
    </ScrollView>
  );

  const renderIndividualCheck = (): JSX.Element => <IndividualCheck subscribeData={subscribeData} />;

  const renderEmpty = (): JSX.Element => (
    <ScrollView style={styles.emptyContainer}>
      <Text>Выберите в меню</Text>
    </ScrollView>
  );

  return useMemo(() => {
    const sheetMap: Record<number, JSX.Element> = {
      [SHEET.SUBSCRIPTION_MONITORING]: renderSubscriptionCheck(),
      [SHEET.COMPANY_CHECK]: renderCompanyCheck(),
      [SHEET.INDIVIDUAL_CHECK]: renderIndividualCheck(),
    };

    return activeSheet ? sheetMap[activeSheet] ?? renderEmpty() : renderEmpty();
  }, [activeSheet, reportId, isLoading, isRefreshReport, subscribeData]);
};

export const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
    width: "100%",
  },
  content: {
    padding: 20,
  },
  emptyContainer: {
    flex: 1,
    padding: 20,
  },
});

export default SheetContentRenderer;
